"use client";

import type { ComponentProps, FC, ReactNode } from "react";
import { useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, type FieldState } from "./field-chrome";

type PhoneInputProps = Omit<ComponentProps<"input">, "onChange" | "value" | "ref"> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    label?: ReactNode;
    message?: ReactNode;
    state?: FieldState;
    onChange?: (value: string) => void;
  };

/** Emits E.164 (`+61412345678`) — the format the API stores, so no reformatting at the call site. */
export const PhoneInput: FC<PhoneInputProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  id,
  value,
  onChange,
  ...props
}) => (
  <div className="flex w-full flex-col gap-8">
    <FieldLabel htmlFor={id} state={state} required={required}>
      {label}
    </FieldLabel>
    <RPNInput.default
      className={cn(
        "flex w-full items-center gap-8 rounded-medium border bg-neutral-white p-16 transition-colors focus-within:border-brand-blue",
        state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
        className,
      )}
      flagComponent={Flag}
      countrySelectComponent={CountrySelect}
      inputComponent={NumberInput}
      smartCaret={false}
      defaultCountry="AU"
      id={id}
      required={required}
      value={value || undefined}
      /* RPN reports an incomplete number as `undefined`; react-hook-form needs a string to validate. */
      onChange={(next) => onChange?.(next ?? "")}
      {...props}
    />
    <FieldMessage state={state}>{message}</FieldMessage>
  </div>
);

const NumberInput: FC<ComponentProps<"input">> = ({ className, ...props }) => (
  <input
    className={cn(
      "min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35",
      className,
    )}
    {...props}
  />
);

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: { label: string; value: RPNInput.Country | undefined }[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect: FC<CountrySelectProps> = ({ disabled, value: selected, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const countries = options.filter(
    (option): option is { label: string; value: RPNInput.Country } =>
      Boolean(option.value) && option.label.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <RadixPopover.Root
      /* Without this the wheel scrolls whatever the field sits inside (a drawer, a page) instead
         of the country list — `modal` locks scrolling everywhere outside the popover. */
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setSearch("");
      }}
    >
      <RadixPopover.Trigger
        type="button"
        disabled={disabled}
        className="flex shrink-0 cursor-pointer items-center gap-4 border-r border-neutral-light-grey pr-8 text-16 text-neutral-black outline-none disabled:cursor-not-allowed"
      >
        <Flag country={selected} countryName={selected} />
        <ChevronDownIcon className={cn("size-16 text-neutral-black", disabled && "hidden")} />
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          /* Content-sized like every other dropdown, but floored at 300 px rather than at the
             trigger — the trigger here is a flag glyph, which is no basis for a country list. */
          className="z-50 w-max min-w-300 max-w-[min(560px,calc(100vw-32px))] overflow-hidden rounded-small border border-neutral-light-grey bg-neutral-white shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]"
        >
          {/* TODO: typing into this is broken — parked, so the list shows every country unfiltered.
          <input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search country..."
            className="w-full border-b border-neutral-light-grey px-16 py-12 text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
          /> */}
          <ScrollArea className="h-288">
            {countries.length === 0 && <p className="px-16 py-12 text-16 text-neutral-grey">No country found.</p>}
            <div className="flex flex-col p-4">
              {countries.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    onChange(value);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-8 rounded-small px-16 py-12 text-left text-16 text-neutral-black outline-none transition-colors duration-100 hover:bg-tone-aqua-tint"
                >
                  <Flag country={value} countryName={label} />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <span className="shrink-0 text-neutral-grey">+{RPNInput.getCountryCallingCode(value)}</span>
                  <CheckIcon className={cn("size-20 shrink-0", value === selected ? "opacity-100" : "opacity-0")} />
                </button>
              ))}
            </div>
          </ScrollArea>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
};

const Flag = ({ country, countryName }: RPNInput.FlagProps) => {
  const CountryFlag = flags[country];

  return (
    <span className="flex h-16 w-24 shrink-0 overflow-hidden rounded-xs bg-neutral-light-grey [&_svg]:size-full">
      {CountryFlag && <CountryFlag title={countryName} />}
    </span>
  );
};
